// "Local" or "Staging" chip outside production (FR-038; US5-5); nothing in production.
import { Platform, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { type AppEnv, ConfigError, readPublicConfig } from "@/config";
import { makeStyles } from "@/theme/makeStyles";
import { Text } from "@/ui/Text";

const LABEL: Record<AppEnv, string | null> = {
  local: "Local",
  staging: "Staging",
  production: null,
};

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    badge: {
      alignSelf: "flex-start",
      margin: t.space.xs,
      paddingHorizontal: t.space.smd,
      paddingVertical: t.space.xxs / 2,
      borderRadius: t.radius.pill,
      backgroundColor: t.color.envBadge,
    },
    // Android: a small chip floating just under the status bar, over the top margin of the header
    // or app bar, so non-production builds keep the production layout (and the sign-in backdrop can
    // still run under the status bar).
    overlay: {
      position: "absolute",
      left: 0,
      right: 0,
      alignItems: "center",
      zIndex: 30,
    },
    chip: { alignSelf: "center", margin: 0 },
  }),
);

function currentEnv(): AppEnv | null {
  try {
    return readPublicConfig().appEnv;
  } catch (error) {
    if (error instanceof ConfigError) return null;
    throw error;
  }
}

export function EnvBadge({ appEnv }: { appEnv?: AppEnv }) {
  const styles = useStyles();
  const insets = useSafeAreaInsets();
  const env = appEnv ?? currentEnv();
  const label = env ? LABEL[env] : null;
  if (!label) return null;
  const native = Platform.OS !== "web";
  const badge = (
    // A labelled landmark, so axe's region rule holds on every page (2026-10-06).
    <View
      style={[styles.badge, native && styles.chip]}
      testID="env-badge"
      role="complementary"
      aria-label="Environment"
    >
      <Text size="xxs" weight="semiBold">
        {label}
      </Text>
    </View>
  );
  if (!native) return badge;
  return (
    <View style={[styles.overlay, { top: insets.top }]} pointerEvents="none">
      {badge}
    </View>
  );
}
